import React from 'react';
import { Box, Container, Paper, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Celebration as CelebrationIcon } from '@mui/icons-material';

type Props = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
};

/** Shared TextField / form controls when rendered on AuthShell’s light surface (explore-aligned). */
const AUTH_INPUT_BG = '#fffcf7';
const AUTH_AUTOFILL = '#fdf8f0';

export const authFormSurfaceSx: SxProps<Theme> = {
  width: '100%',
  '& .MuiInputLabel-root': {
    color: 'rgba(46, 38, 28, 0.55)',
    transition: 'color 0.2s ease, transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#9a7318', fontWeight: 600 },
  '& .MuiInputLabel-root.MuiInputLabel-shrink:not(.Mui-focused):not(.Mui-error)': {
    color: 'rgba(46, 38, 28, 0.62)',
    fontWeight: 600,
  },
  '& .MuiInputLabel-root.Mui-error': { color: '#b85c5c' },
  '& .MuiFormLabel-root': { color: 'rgba(46, 38, 28, 0.55)' },
  '& .MuiFormLabel-root.Mui-focused': { color: '#9a7318' },
  '& .MuiInputBase-input': {
    color: '#2e261c',
    caretColor: '#7a5c1a',
    transition: 'color 0.15s ease',
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: AUTH_INPUT_BG,
    borderRadius: '12px',
    transition: 'background-color 0.25s ease, box-shadow 0.22s ease',
    '& fieldset': { borderColor: 'rgba(139, 105, 20, 0.22)', transition: 'border-color 0.2s ease' },
    '&:hover': {
      backgroundColor: '#fffefa',
      boxShadow: '0 4px 18px rgba(46, 38, 28, 0.06)',
    },
    '&:hover fieldset': { borderColor: 'rgba(139, 105, 20, 0.38)' },
    '&.Mui-focused': {
      backgroundColor: '#fffefb',
      boxShadow: [
        '0 0 0 3px rgba(201, 162, 39, 0.25)',
        '0 8px 28px rgba(201, 162, 39, 0.14)',
      ].join(', '),
    },
    '&.Mui-focused fieldset': {
      borderColor: '#c9a227',
      borderWidth: '2px',
    },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(246, 240, 230, 0.55)',
    },
  },
  '& .MuiOutlinedInput-root .MuiOutlinedInput-input': {
    /* Kill Chrome/Safari autofill blue-yellow; stay on-brand */
    '&:-webkit-autofill': {
      WebkitBoxShadow: `0 0 0 1000px ${AUTH_AUTOFILL} inset`,
      WebkitTextFillColor: '#2e261c',
      caretColor: '#7a5c1a',
      borderRadius: '10px',
      transition: 'background-color 99999s ease-out 0s',
    },
    '&:-webkit-autofill:hover': {
      WebkitBoxShadow: `0 0 0 1000px ${AUTH_AUTOFILL} inset`,
      WebkitTextFillColor: '#2e261c',
    },
    '&:-webkit-autofill:focus': {
      WebkitBoxShadow: `0 0 0 1000px #fffefb inset`,
      WebkitTextFillColor: '#2e261c',
    },
  },
  '& .MuiFormHelperText-root': { color: 'rgba(46, 38, 28, 0.5)' },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    color: '#b8860b !important',
    transition: 'color 0.2s ease, transform 0.2s ease',
  },
  '& .MuiOutlinedInput-root:focus-within .MuiInputAdornment-root .MuiSvgIcon-root': {
    color: '#7a5c1a !important',
    transform: 'scale(1.08)',
  },
  '& .MuiSelect-icon': { color: '#b8860b' },
  '& .Mui-disabled': {
    WebkitTextFillColor: 'rgba(46, 38, 28, 0.45) !important',
    color: 'rgba(46, 38, 28, 0.45) !important',
    backgroundColor: 'rgba(246, 240, 230, 0.55)',
  },
};

/**
 * Shared auth page wrapper — warm cream page + linen card like ClientExplore Chaavi shell.
 */
const AuthShell: React.FC<Props> = ({ title, subtitle, children }) => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 3, sm: 4 },
        px: 2,
        background:
          'radial-gradient(ellipse 95% 62% at 50% 0%, rgba(201, 162, 39, 0.14) 0%, transparent 56%), linear-gradient(168deg, #fdfbf7 0%, #f5efe6 38%, #efe7db 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 80% 55% at 100% 100%, rgba(184, 92, 92, 0.06) 0%, transparent 50%), radial-gradient(ellipse 55% 45% at 0% 90%, rgba(201, 162, 39, 0.08) 0%, transparent 45%)',
          pointerEvents: 'none',
          zIndex: 0,
        },
      }}
    >
      <Container
        component="main"
        maxWidth={false}
        disableGutters
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: { xs: '100%', sm: 560 },
          mx: 'auto',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            color: '#2e261c',
            padding: { xs: 3, sm: 4 },
            width: '100%',
            borderRadius: '22px',
            background: 'linear-gradient(180deg, #fffefb 0%, #faf6f1 52%, #f7f2ea 100%)',
            backdropFilter: 'blur(12px)',
            boxShadow:
              '0 1px 0 rgba(255, 255, 255, 0.95) inset, 0 16px 48px rgba(46, 38, 28, 0.07), 0 4px 14px rgba(46, 38, 28, 0.04)',
            border: '1px solid rgba(139, 105, 20, 0.16)',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              sx={{
                width: { xs: 76, sm: 86 },
                height: { xs: 76, sm: 86 },
                borderRadius: '50%',
                background: 'linear-gradient(145deg, #d4af37 0%, #c9a227 45%, #e8cf6b 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                boxShadow: '0 8px 28px rgba(201, 162, 39, 0.35), 0 1px 0 rgba(255,255,255,0.35) inset',
                border: '1px solid rgba(255, 255, 255, 0.45)',
              }}
            >
              <CelebrationIcon sx={{ fontSize: 40, color: '#2a2318' }} />
            </Box>

            <Typography
              component="h1"
              variant="h4"
              gutterBottom
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontWeight: 500,
                color: '#7a5c1a',
                textAlign: 'center',
              }}
            >
              {title}
            </Typography>

            {subtitle ? (
              <Typography
                variant="body2"
                align="center"
                sx={{
                  mb: 3,
                  color: 'rgba(46, 38, 28, 0.58)',
                  lineHeight: 1.55,
                  maxWidth: 360,
                }}
              >
                {subtitle}
              </Typography>
            ) : (
              <Box sx={{ height: 6 }} />
            )}

            {children}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AuthShell;
